import React, { FunctionComponent, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { PillowTalkProvider, useStartConversation } from '@spotify-internal/pillow-talk';
import { captureException } from '@sentry/nextjs';

import { useVerticalConfig } from 'providers/vertical';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { MESSAGING_ENABLED } from 'utils/config';

import { DEFAULT_STATUS, DISABLED_STATUS, MessagingAvailabilityReason, MessagingType } from './types';
import { MessagingContext } from './MessagingContext';

const PillowTalkMessagingProvider: FunctionComponent = ({ children }) => {
  const shouldSetStatus = useRef(true);
  const [status, setStatus] = useState(MESSAGING_ENABLED ? DEFAULT_STATUS : DISABLED_STATUS);

  const { verticalName: vertical, features } = useVerticalConfig();
  const { lineOfBusiness = '' } = features.configurePillowTalk || {};

  const { startConversation, loading, error } = useStartConversation(lineOfBusiness);

  const value = useMemo<MessagingType>(
    () => ({
      status,
      startMessaging: status.isAvailable
        ? async () => {
            try {
              await startConversation();
              sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_START, { vertical });
            } catch (err) {
              captureException(err);
            }
          }
        : undefined,
    }),
    [status, startConversation, vertical],
  );

  useEffect(() => {
    if (!MESSAGING_ENABLED || !shouldSetStatus.current || loading) {
      return;
    }

    shouldSetStatus.current = false;

    if (!error) {
      const resolution = MessagingAvailabilityReason.AVAILABLE;
      setStatus({ isAvailable: true, reason: resolution });
      sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
    } else {
      const resolution = MessagingAvailabilityReason.PILLOW_TALK_ERROR;
      setStatus({ isAvailable: false, reason: resolution });
      sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
    }
  }, [loading, error, vertical]);

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
};

export const PillowTalkWrapper: FunctionComponent = ({ children }) => {
  const getToken = useCallback(async () => {
    if (typeof window !== 'undefined') {
      return (await import('utils/client/auth/auth')).getPillowTalkAuthToken();
    }
    return '';
  }, []);

  return (
    <PillowTalkProvider token={getToken()}>
      <PillowTalkMessagingProvider>{children}</PillowTalkMessagingProvider>
    </PillowTalkProvider>
  );
};
